import type { KLineData } from "kchart";
import { stockKlineBarsToKLineData } from "fin-web-components";
import dayjs from "dayjs";
import type {
  TacticsDailyScorePointDTO,
  TacticsStockKlineBarDTO,
  TacticsUserSignalSettingsDTO,
} from "./TacticsTypes";

function dayKeyFromTradeTime(tradeTime: string | number): string {
  if (typeof tradeTime === "number" && Number.isFinite(tradeTime)) {
    const d = new Date(tradeTime);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  }
  const s = String(tradeTime).trim();
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function tacticsApiDateTimeString(v: unknown): string {
  if (v == null || v === "") {
    return "";
  }
  if (typeof v === "string") {
    return v.trim();
  }
  if (typeof v === "number" && Number.isFinite(v)) {
    return dayjs(v).format("YYYY-MM-DD HH:mm:ss");
  }
  if (v instanceof Date) {
    return dayjs(v).format("YYYY-MM-DD HH:mm:ss");
  }
  return String(v).trim();
}

function dayKeyFromTradeDate(tradeDate: unknown): string {
  const s = tacticsApiDateTimeString(tradeDate);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

export function tacticsDayKeyFromKlineTime(tradeTime: string | number): string {
  return dayKeyFromTradeTime(tradeTime);
}

export function findTacticsAssessForKlineTime(
  assessPoints: TacticsDailyScorePointDTO[],
  klineTimeMs: number
): TacticsDailyScorePointDTO | null {
  const dayKey = dayKeyFromTradeTime(klineTimeMs);
  if (!dayKey) {
    return null;
  }
  for (const p of assessPoints) {
    if (dayKeyFromTradeDate(p?.tradeDate) === dayKey) {
      return p;
    }
  }
  return null;
}

function iconMarkContent(url: string): string {
  const u = url.trim();
  if (!u) {
    return "";
  }
  return `<img src="${u}" alt="" style="width:16px;height:16px;display:block" />`;
}

function hasSignalDisplayMode(settings: TacticsUserSignalSettingsDTO | null): boolean {
  if (!settings) {
    return false;
  }
  const opp =
    typeof settings.opportunityThreshold === "number" &&
    Number.isFinite(settings.opportunityThreshold) &&
    !!settings.opportunityIconUrl?.trim();
  const risk =
    typeof settings.riskThreshold === "number" &&
    Number.isFinite(settings.riskThreshold) &&
    !!settings.riskIconUrl?.trim();
  return opp || risk;
}

export function buildTacticsKlineData(
  bars: TacticsStockKlineBarDTO[],
  assessPoints: TacticsDailyScorePointDTO[],
  signalSettings: TacticsUserSignalSettingsDTO | null
): KLineData[] {
  const assessByDay = new Map<string, TacticsDailyScorePointDTO>();
  for (const p of assessPoints) {
    const dayKey = dayKeyFromTradeDate(p?.tradeDate);
    if (!dayKey) {
      continue;
    }
    assessByDay.set(dayKey, p);
  }

  const base = stockKlineBarsToKLineData(
    bars.map((b) => ({
      tradeTime: b.tradeTime,
      segType: b.segType,
      openPrice: b.openPrice,
      highPrice: b.highPrice,
      lowPrice: b.lowPrice,
      closePrice: b.closePrice,
      changeRatio: b.changeRatio,
      volume: b.volume,
    }))
  );

  const signalMode = hasSignalDisplayMode(signalSettings);
  const oppThr = signalSettings?.opportunityThreshold ?? null;
  const riskThr = signalSettings?.riskThreshold ?? null;
  const oppIcon = signalSettings?.opportunityIconUrl?.trim() || "";
  const riskIcon = signalSettings?.riskIconUrl?.trim() || "";

  return base.map((k) => {
    const dayKey = dayKeyFromTradeTime(k.time);
    const assess = assessByDay.get(dayKey);
    if (!assess) {
      return k;
    }

    const marks: NonNullable<KLineData["marks"]> = [];

    if (signalMode) {
      const oppScore = assess.score;
      if (
        oppIcon &&
        oppThr != null &&
        oppScore != null &&
        Number.isFinite(oppScore) &&
        oppScore >= oppThr
      ) {
        marks.push({
          type: "bottom",
          name: "机会",
          content: iconMarkContent(oppIcon),
        });
      }
      const riskScore = assess.riskScore;
      if (
        riskIcon &&
        riskThr != null &&
        riskScore != null &&
        Number.isFinite(riskScore) &&
        riskScore >= riskThr
      ) {
        marks.push({
          type: "top",
          name: "风险",
          content: iconMarkContent(riskIcon),
        });
      }
    }

    return marks.length ? { ...k, marks } : k;
  });
}
